import { useNavigate } from "@tanstack/react-router";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Download,
  FileX,
  Folder,
  FolderOpen,
  Search,
  Share2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { CreateShareDialog } from "@/components/document-sharing/CreateShareDialog";
import { FolderUploadZone } from "@/components/projects/FolderUploadZone";
import { ErrorState } from "@/components/shared/error-state";
import { FileIcon, formatFileSize } from "@/components/shared/file-display";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { type ProjectFile, useFolderFiles, useUnfiledFiles } from "@/hooks/use-files";
import { type ProjectFolder, useProjectFolders } from "@/hooks/use-folders";
import {
  type FileTypeBucket,
  type TaskDocumentRow,
  type TaskDocumentSortField,
  type TaskStatus,
  useProjectTaskDocumentFacets,
  useProjectTaskDocuments,
} from "@/hooks/use-task-documents";
import { cn } from "@/lib/utils";

// ── Shared helpers ──

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ── Files sub-tab ──

const TASK_STATUS_OPTIONS: Array<{ value: TaskStatus; label: string }> = [
  { value: "not_started", label: "Not started" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "blocked", label: "Blocked" },
  { value: "skipped", label: "Skipped" },
  { value: "overdue", label: "Overdue" },
];

const FILE_TYPE_OPTIONS: Array<{ value: FileTypeBucket; label: string }> = [
  { value: "pdf", label: "PDF" },
  { value: "image", label: "Image" },
  { value: "office", label: "Office doc" },
  { value: "other", label: "Other" },
];

function isPreviewable(mimeType: string | null): "image" | "pdf" | null {
  if (!mimeType) return null;
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType === "application/pdf") return "pdf";
  return null;
}

interface MultiSelectFilterProps {
  label: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
}

function MultiSelectFilter({ label, options, selected, onChange }: MultiSelectFilterProps) {
  const selectedSet = new Set(selected);
  function toggle(value: string) {
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(Array.from(next));
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          {label}
          {selected.length > 0 && (
            <Badge variant="secondary" className="h-5 px-1.5">
              {selected.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        {options.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-muted-foreground">No options</p>
        ) : (
          <div className="max-h-64 overflow-y-auto">
            {options.map((opt) => (
              <label
                key={opt.value}
                htmlFor={`filter-opt-${opt.value}`}
                className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
              >
                <Checkbox
                  id={`filter-opt-${opt.value}`}
                  checked={selectedSet.has(opt.value)}
                  onCheckedChange={() => toggle(opt.value)}
                />
                <span className="truncate">{opt.label}</span>
              </label>
            ))}
          </div>
        )}
        {selected.length > 0 && (
          <div className="border-t pt-1 mt-1">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-xs"
              onClick={() => onChange([])}
            >
              <X className="mr-1 h-3 w-3" />
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface FilterState {
  search: string;
  taskStatus: TaskStatus[];
  milestoneId: string[];
  uploaderUserId: string[];
  uploaderCompanyId: string[];
  fileType: FileTypeBucket[];
  from: string | null;
  to: string | null;
  sortBy: TaskDocumentSortField;
  sortDir: "asc" | "desc";
}

const INITIAL_FILTERS: FilterState = {
  search: "",
  taskStatus: [],
  milestoneId: [],
  uploaderUserId: [],
  uploaderCompanyId: [],
  fileType: [],
  from: null,
  to: null,
  sortBy: "createdAt",
  sortDir: "desc",
};

interface SortableHeaderProps {
  field: TaskDocumentSortField;
  label: string;
  currentSort: TaskDocumentSortField;
  currentDir: "asc" | "desc";
  onSort: (field: TaskDocumentSortField) => void;
  className?: string;
}

function SortableHeader({
  field,
  label,
  currentSort,
  currentDir,
  onSort,
  className,
}: SortableHeaderProps) {
  const isActive = currentSort === field;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className="inline-flex items-center gap-1 font-medium hover:text-foreground"
      >
        {label}
        {isActive ? (
          currentDir === "desc" ? (
            <ArrowDown className="h-3 w-3" />
          ) : (
            <ArrowUp className="h-3 w-3" />
          )
        ) : null}
      </button>
    </TableHead>
  );
}

interface PreviewState {
  file: TaskDocumentRow;
  blobUrl: string | null;
  loading: boolean;
  error: string | null;
}

function FilesSubTab({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS);
  const debouncedSearch = useDebouncedValue(filters.search, 300);

  const queryFilters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      taskStatus: filters.taskStatus.length > 0 ? filters.taskStatus : undefined,
      milestoneId: filters.milestoneId.length > 0 ? filters.milestoneId : undefined,
      uploaderUserId: filters.uploaderUserId.length > 0 ? filters.uploaderUserId : undefined,
      uploaderCompanyId:
        filters.uploaderCompanyId.length > 0 ? filters.uploaderCompanyId : undefined,
      fileType: filters.fileType.length > 0 ? filters.fileType : undefined,
      from: filters.from ?? undefined,
      to: filters.to ?? undefined,
      sortBy: filters.sortBy,
      sortDir: filters.sortDir,
      limit: 100,
    }),
    [
      debouncedSearch,
      filters.taskStatus,
      filters.milestoneId,
      filters.uploaderUserId,
      filters.uploaderCompanyId,
      filters.fileType,
      filters.from,
      filters.to,
      filters.sortBy,
      filters.sortDir,
    ],
  );

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useProjectTaskDocuments(projectId, queryFilters);
  const { data: facets } = useProjectTaskDocumentFacets(projectId);

  const rows = useMemo(() => data?.pages.flatMap((p) => p.data) ?? [], [data]);
  const totalLoaded = rows.length;

  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [shareTarget, setShareTarget] = useState<{ fileId: string; fileName: string } | null>(null);
  const sentinelRef = useRef<HTMLTableRowElement | null>(null);
  const previewAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !isFetchingNextPage) {
            fetchNextPage();
          }
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    return () => {
      if (preview?.blobUrl) URL.revokeObjectURL(preview.blobUrl);
    };
  }, [preview?.blobUrl]);

  // Abort any in-flight preview fetch on unmount.
  useEffect(() => {
    return () => previewAbortRef.current?.abort();
  }, []);

  function handleSort(field: TaskDocumentSortField) {
    setFilters((prev) => {
      if (prev.sortBy === field) {
        return { ...prev, sortDir: prev.sortDir === "asc" ? "desc" : "asc" };
      }
      return { ...prev, sortBy: field, sortDir: field === "createdAt" ? "desc" : "asc" };
    });
  }

  function resetFilters() {
    setFilters(INITIAL_FILTERS);
  }

  async function openPreview(file: TaskDocumentRow) {
    const kind = isPreviewable(file.mimeType);
    if (!kind) {
      previewAbortRef.current?.abort();
      previewAbortRef.current = null;
      setPreview({ file, blobUrl: null, loading: false, error: null });
      return;
    }
    // Cancel any prior in-flight preview fetch — rapid clicks across files
    // must not let an older blob land after a newer one.
    previewAbortRef.current?.abort();
    const controller = new AbortController();
    previewAbortRef.current = controller;
    setPreview({ file, blobUrl: null, loading: true, error: null });
    try {
      const res = await fetch(`/api/files/${file.fileId}`, { signal: controller.signal });
      if (!res.ok) throw new Error(`Failed to load file (${res.status})`);
      const blob = await res.blob();
      const typedBlob =
        file.mimeType && blob.type !== file.mimeType
          ? new Blob([blob], { type: file.mimeType })
          : blob;
      const url = URL.createObjectURL(typedBlob);
      setPreview({ file, blobUrl: url, loading: false, error: null });
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      setPreview({
        file,
        blobUrl: null,
        loading: false,
        error: err instanceof Error ? err.message : "Failed to load preview",
      });
    }
  }

  function closePreview() {
    previewAbortRef.current?.abort();
    if (preview?.blobUrl) URL.revokeObjectURL(preview.blobUrl);
    setPreview(null);
  }

  function downloadFile(file: TaskDocumentRow) {
    window.location.href = `/api/files/${file.fileId}`;
  }

  function goToTask(taskId: string) {
    navigate({
      to: "/projects/$projectId",
      params: { projectId },
      search: { tab: "tasks" },
      hash: `task-${taskId}`,
    });
  }

  const activeFilterCount =
    (filters.search ? 1 : 0) +
    filters.taskStatus.length +
    filters.milestoneId.length +
    filters.uploaderUserId.length +
    filters.uploaderCompanyId.length +
    filters.fileType.length +
    (filters.from ? 1 : 0) +
    (filters.to ? 1 : 0);

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search filename or task..."
            value={filters.search}
            onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
            className="pl-8"
          />
        </div>
        <MultiSelectFilter
          label="Task status"
          options={TASK_STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          selected={filters.taskStatus}
          onChange={(next) => setFilters((p) => ({ ...p, taskStatus: next as TaskStatus[] }))}
        />
        <MultiSelectFilter
          label="Milestone"
          options={(facets?.milestones ?? []).map((m) => ({ value: m.id, label: m.name }))}
          selected={filters.milestoneId}
          onChange={(next) => setFilters((p) => ({ ...p, milestoneId: next }))}
        />
        <MultiSelectFilter
          label="Uploader"
          options={(facets?.uploaders ?? []).map((u) => ({ value: u.id, label: u.name }))}
          selected={filters.uploaderUserId}
          onChange={(next) => setFilters((p) => ({ ...p, uploaderUserId: next }))}
        />
        <MultiSelectFilter
          label="Company"
          options={(facets?.uploaderCompanies ?? []).map((c) => ({
            value: c.id,
            label: c.name,
          }))}
          selected={filters.uploaderCompanyId}
          onChange={(next) => setFilters((p) => ({ ...p, uploaderCompanyId: next }))}
        />
        <MultiSelectFilter
          label="File type"
          options={FILE_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          selected={filters.fileType}
          onChange={(next) => setFilters((p) => ({ ...p, fileType: next as FileTypeBucket[] }))}
        />
        <DatePicker
          value={filters.from}
          onChange={(v) => setFilters((p) => ({ ...p, from: v }))}
          placeholder="From date"
          size="sm"
          ariaLabel="From date"
        />
        <DatePicker
          value={filters.to}
          onChange={(v) => setFilters((p) => ({ ...p, to: v }))}
          placeholder="To date"
          size="sm"
          ariaLabel="To date"
        />
        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={resetFilters} className="gap-1">
            <X className="h-3 w-3" />
            Clear ({activeFilterCount})
          </Button>
        )}
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {["a", "b", "c", "d", "e", "f", "g", "h"].map((k) => (
            <Skeleton key={`docs-skeleton-${k}`} className="h-10 w-full" />
          ))}
        </div>
      ) : isError ? (
        <ErrorState message={error?.message} onRetry={() => refetch()} />
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FileX className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <p className="text-lg font-medium text-muted-foreground">
              {activeFilterCount > 0
                ? "No documents match your filters"
                : "No task evidence has been uploaded to this project yet"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableHeader
                  field="fileName"
                  label="Name"
                  currentSort={filters.sortBy}
                  currentDir={filters.sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  field="taskTitle"
                  label="Task"
                  currentSort={filters.sortBy}
                  currentDir={filters.sortDir}
                  onSort={handleSort}
                />
                <TableHead>Milestone</TableHead>
                <SortableHeader
                  field="uploaderName"
                  label="Uploader"
                  currentSort={filters.sortBy}
                  currentDir={filters.sortDir}
                  onSort={handleSort}
                />
                <TableHead>Company</TableHead>
                <SortableHeader
                  field="createdAt"
                  label="Uploaded"
                  currentSort={filters.sortBy}
                  currentDir={filters.sortDir}
                  onSort={handleSort}
                  className="w-32"
                />
                <TableHead className="w-20 text-right">Size</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.fileId}>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => openPreview(row)}
                      className="flex items-center gap-2 text-left hover:underline"
                    >
                      <FileIcon mimeType={row.mimeType} />
                      <span className="truncate max-w-sm">{row.fileName}</span>
                    </button>
                  </TableCell>
                  <TableCell>
                    <button
                      type="button"
                      onClick={() => goToTask(row.taskId)}
                      className="text-left hover:underline"
                    >
                      <span className="truncate">{row.taskTitle ?? "—"}</span>
                    </button>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {row.milestoneName ?? "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{row.uploaderName ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {row.uploaderCompanyName ?? "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(row.createdAt)}
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {formatFileSize(row.fileSizeBytes)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() => downloadFile(row)}
                        aria-label="Download"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0"
                        onClick={() =>
                          setShareTarget({ fileId: row.fileId, fileName: row.fileName })
                        }
                        aria-label="Share"
                        title="Share this document"
                      >
                        <Share2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow ref={sentinelRef}>
                <TableCell colSpan={8} className="py-2 text-center text-xs text-muted-foreground">
                  {isFetchingNextPage
                    ? "Loading more..."
                    : hasNextPage
                      ? ""
                      : `${totalLoaded} document${totalLoaded === 1 ? "" : "s"}`}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}

      {/* Preview dialog */}
      <Dialog open={preview !== null} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="w-[50vw] max-w-[50vw] sm:max-w-[50vw] h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {preview && <FileIcon mimeType={preview.file.mimeType} />}
              <span className="truncate">{preview?.file.fileName}</span>
            </DialogTitle>
            <DialogDescription className="text-xs">
              {preview?.file.taskTitle ? `From task: ${preview.file.taskTitle}` : null}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {preview?.loading && <Skeleton className="h-full w-full" />}
            {preview?.error && (
              <div className="flex h-full flex-col items-center justify-center gap-3">
                <p className="text-sm text-destructive">{preview.error}</p>
                <Button variant="outline" size="sm" onClick={() => downloadFile(preview.file)}>
                  <Download className="mr-2 h-4 w-4" /> Download instead
                </Button>
              </div>
            )}
            {preview?.blobUrl &&
              (isPreviewable(preview.file.mimeType) === "image" ? (
                <div className="flex h-full items-center justify-center overflow-auto bg-muted/30">
                  <img
                    src={preview.blobUrl}
                    alt={preview.file.fileName}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              ) : (
                <iframe
                  title={preview.file.fileName}
                  src={preview.blobUrl}
                  className="h-full w-full"
                />
              ))}
            {preview && !preview.loading && !preview.error && !preview.blobUrl && (
              <div className="flex h-full flex-col items-center justify-center gap-3">
                <p className="text-sm text-muted-foreground">
                  Preview is not available for this file type.
                </p>
                <Button variant="outline" size="sm" onClick={() => downloadFile(preview.file)}>
                  <Download className="mr-2 h-4 w-4" /> Download
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {shareTarget && (
        <CreateShareDialog
          open={!!shareTarget}
          onOpenChange={(open) => {
            if (!open) setShareTarget(null);
          }}
          projectId={projectId}
          lockedFileId={shareTarget.fileId}
          lockedFileName={shareTarget.fileName}
        />
      )}
    </div>
  );
}

// ── Folders sub-tab ──

interface FolderNode {
  folder: ProjectFolder;
  children: FolderNode[];
  depth: number;
}

function buildFolderTree(folders: ProjectFolder[]): FolderNode[] {
  const sorted = [...folders].sort((a, b) => {
    const ao = a.displayOrder ?? 0;
    const bo = b.displayOrder ?? 0;
    if (ao !== bo) return ao - bo;
    return (a.fullPath ?? "").localeCompare(b.fullPath ?? "");
  });

  const roots: FolderNode[] = [];
  const nodeMap = new Map<string, FolderNode>();

  for (const folder of sorted) {
    const path = folder.fullPath ?? "";
    const parts = path.split("/").filter(Boolean);
    const depth = parts.length - 1;
    const node: FolderNode = { folder, children: [], depth };
    nodeMap.set(path, node);

    if (depth === 0) {
      roots.push(node);
    } else {
      const parentPath = parts.slice(0, -1).join("/");
      const parent = nodeMap.get(parentPath);
      if (parent) {
        parent.children.push(node);
      } else {
        roots.push(node);
      }
    }
  }

  return roots;
}

/**
 * Total file count for a node, summing the node's own files plus every
 * descendant's files. Used to render a rollup badge on parent folders so the
 * tree shows "01 - Preconstruction · 3" when the only file lives in
 * 01 - Preconstruction / Proposals.
 */
function rollupFileCount(node: FolderNode): number {
  let total = node.folder.fileCount ?? 0;
  for (const child of node.children) total += rollupFileCount(child);
  return total;
}

type SelectedEntry = string | "__unfiled__";

function FolderTreeItem({
  node,
  expanded,
  selected,
  onToggle,
  onSelect,
}: {
  node: FolderNode;
  expanded: Set<string>;
  selected: SelectedEntry | null;
  onToggle: (id: string) => void;
  onSelect: (id: SelectedEntry) => void;
}) {
  const isExpanded = expanded.has(node.folder.id);
  const isSelected = selected === node.folder.id;
  const hasChildren = node.children.length > 0;
  const name = (node.folder.fullPath ?? "").split("/").pop() ?? "Unnamed";

  function handleRowClick() {
    onSelect(node.folder.id);
    if (hasChildren) onToggle(node.folder.id);
  }

  return (
    <div>
      <button
        type="button"
        className={cn(
          "group flex w-full items-center rounded-md text-left text-sm transition-colors",
          isSelected
            ? "bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:text-amber-100"
            : "hover:bg-muted/60",
        )}
        style={{ paddingLeft: `${node.depth * 14}px` }}
        onClick={handleRowClick}
        aria-expanded={hasChildren ? isExpanded : undefined}
      >
        {/* Chevron — stops propagation so it only toggles, not selects */}
        <span
          className="flex h-8 w-5 shrink-0 items-center justify-center"
          onClick={(e) => {
            if (hasChildren) {
              e.stopPropagation();
              onToggle(node.folder.id);
            }
          }}
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
            )
          ) : (
            <span className="w-3.5" />
          )}
        </span>

        {/* Folder icon + name */}
        <span className="flex flex-1 items-center gap-1.5 py-1.5 pr-2 min-w-0">
          {isExpanded && hasChildren ? (
            <FolderOpen className="h-4 w-4 shrink-0 text-amber-500" />
          ) : (
            <Folder className="h-4 w-4 shrink-0 text-amber-500" />
          )}
          <span
            className={cn(
              "flex-1 text-xs leading-tight",
              isSelected ? "font-semibold" : "font-medium",
            )}
          >
            {name}
          </span>
          {(() => {
            const total = rollupFileCount(node);
            return total > 0 ? (
              <Badge
                variant={isSelected ? "default" : "secondary"}
                className={cn(
                  "ml-auto h-4 px-1 text-[10px] shrink-0",
                  isSelected && "bg-amber-600 text-white",
                )}
              >
                {total}
              </Badge>
            ) : null;
          })()}
        </span>
      </button>

      {isExpanded &&
        node.children.map((child) => (
          <FolderTreeItem
            key={child.folder.id}
            node={child}
            expanded={expanded}
            selected={selected}
            onToggle={onToggle}
            onSelect={onSelect}
          />
        ))}
    </div>
  );
}

interface FolderPreviewState {
  file: ProjectFile;
  blobUrl: string | null;
  loading: boolean;
  error: string | null;
}

function FolderFilesPanel({
  projectId,
  selected,
  selectedName,
}: {
  projectId: string;
  selected: SelectedEntry | null;
  selectedName?: string;
}) {
  const isUnfiled = selected === "__unfiled__";
  const folderId = selected && !isUnfiled ? selected : "";

  const {
    data: folderFiles,
    isLoading: folderLoading,
    isError: folderError,
  } = useFolderFiles(projectId, folderId);

  const {
    data: unfiledFiles,
    isLoading: unfiledLoading,
    isError: unfiledError,
  } = useUnfiledFiles(projectId);

  const [preview, setPreview] = useState<FolderPreviewState | null>(null);
  const [shareTarget, setShareTarget] = useState<{ fileId: string; fileName: string } | null>(null);
  const previewAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (preview?.blobUrl) URL.revokeObjectURL(preview.blobUrl);
    };
  }, [preview?.blobUrl]);

  // Abort any in-flight preview fetch on unmount.
  useEffect(() => {
    return () => previewAbortRef.current?.abort();
  }, []);

  async function openPreview(file: ProjectFile) {
    const kind = isPreviewable(file.mimeType);
    if (!kind) {
      previewAbortRef.current?.abort();
      previewAbortRef.current = null;
      setPreview({ file, blobUrl: null, loading: false, error: null });
      return;
    }
    // Cancel any prior in-flight preview fetch — rapid clicks across files
    // must not let an older blob land after a newer one.
    previewAbortRef.current?.abort();
    const controller = new AbortController();
    previewAbortRef.current = controller;
    setPreview({ file, blobUrl: null, loading: true, error: null });
    try {
      const res = await fetch(`/api/files/${file.id}`, { signal: controller.signal });
      if (!res.ok) throw new Error(`Failed to load file (${res.status})`);
      const blob = await res.blob();
      const typedBlob =
        file.mimeType && blob.type !== file.mimeType
          ? new Blob([blob], { type: file.mimeType })
          : blob;
      setPreview({ file, blobUrl: URL.createObjectURL(typedBlob), loading: false, error: null });
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      setPreview({
        file,
        blobUrl: null,
        loading: false,
        error: err instanceof Error ? err.message : "Failed to load preview",
      });
    }
  }

  function closePreview() {
    previewAbortRef.current?.abort();
    if (preview?.blobUrl) URL.revokeObjectURL(preview.blobUrl);
    setPreview(null);
  }

  function downloadFile(file: ProjectFile) {
    window.location.href = `/api/files/${file.id}`;
  }

  if (!selected) {
    return (
      <div className="flex h-full min-h-[300px] flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <Folder className="h-8 w-8 text-muted-foreground/40" />
        <span>Select a folder to view its files</span>
      </div>
    );
  }

  const files: ProjectFile[] = isUnfiled ? (unfiledFiles ?? []) : (folderFiles ?? []);
  const isLoading = isUnfiled ? unfiledLoading : folderLoading;
  const isError = isUnfiled ? unfiledError : folderError;

  if (isLoading) {
    return (
      <div className="space-y-2 p-1">
        {["a", "b", "c", "d"].map((k) => (
          <Skeleton key={`fp-${k}`} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex h-full min-h-[300px] items-center justify-center text-sm text-destructive">
        Failed to load files.
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex h-full flex-col">
        {selectedName && (
          <div className="mb-3 flex items-center gap-2 border-b pb-3">
            <FolderOpen className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-semibold text-foreground">{selectedName}</span>
            <span className="text-xs text-muted-foreground">— 0 files</span>
          </div>
        )}
        <div className="flex flex-1 min-h-[200px] items-center justify-center text-sm text-muted-foreground">
          No files in this folder
        </div>
      </div>
    );
  }

  return (
    <>
      {selectedName && (
        <div className="mb-3 flex items-center gap-2 border-b pb-3">
          <FolderOpen className="h-4 w-4 text-amber-500" />
          <span className="text-sm font-semibold text-foreground">{selectedName}</span>
          <span className="text-xs text-muted-foreground">
            — {files.length} {files.length === 1 ? "file" : "files"}
          </span>
        </div>
      )}
      <div className="rounded-md border overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-24">Size</TableHead>
              <TableHead className="w-32">Uploaded</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {files.map((file) => (
              <TableRow key={file.id}>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => openPreview(file)}
                    className="flex items-center gap-2 text-left hover:underline"
                  >
                    <FileIcon mimeType={file.mimeType} />
                    <span className="truncate max-w-xs">{file.fileName}</span>
                  </button>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatFileSize(file.fileSizeBytes)}
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {formatDate(file.createdAt)}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => downloadFile(file)}
                      aria-label="Download"
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => setShareTarget({ fileId: file.id, fileName: file.fileName })}
                      aria-label="Share"
                      title="Share this document"
                    >
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={preview !== null} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="w-[50vw] max-w-[50vw] sm:max-w-[50vw] h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {preview && <FileIcon mimeType={preview.file.mimeType} />}
              <span className="truncate">{preview?.file.fileName}</span>
            </DialogTitle>
            <DialogDescription asChild>
              <Button
                variant="outline"
                size="sm"
                className="w-fit"
                onClick={() => preview && downloadFile(preview.file)}
              >
                <Download className="mr-2 h-4 w-4" />
                Download
              </Button>
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {preview?.loading && <Skeleton className="h-full w-full" />}
            {preview?.error && (
              <div className="flex h-full flex-col items-center justify-center gap-3">
                <p className="text-sm text-destructive">{preview.error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => preview && downloadFile(preview.file)}
                >
                  <Download className="mr-2 h-4 w-4" /> Download instead
                </Button>
              </div>
            )}
            {preview?.blobUrl &&
              (isPreviewable(preview.file.mimeType) === "image" ? (
                <div className="flex h-full items-center justify-center overflow-auto bg-muted/30">
                  <img
                    src={preview.blobUrl}
                    alt={preview.file.fileName}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              ) : (
                <iframe
                  title={preview.file.fileName}
                  src={preview.blobUrl}
                  className="h-full w-full"
                />
              ))}
            {preview && !preview.loading && !preview.error && !preview.blobUrl && (
              <div className="flex h-full flex-col items-center justify-center gap-3">
                <p className="text-sm text-muted-foreground">
                  Preview is not available for this file type.
                </p>
                <Button variant="outline" size="sm" onClick={() => downloadFile(preview.file)}>
                  <Download className="mr-2 h-4 w-4" /> Download
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {shareTarget && (
        <CreateShareDialog
          open={!!shareTarget}
          onOpenChange={(open) => {
            if (!open) setShareTarget(null);
          }}
          projectId={projectId}
          lockedFileId={shareTarget.fileId}
          lockedFileName={shareTarget.fileName}
        />
      )}
    </>
  );
}

function FoldersSubTab({ projectId }: { projectId: string }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<SelectedEntry | null>(null);
  const [search, setSearch] = useState("");

  const { data: folders, isLoading, isError, error, refetch } = useProjectFolders(projectId);
  const { data: unfiledFiles } = useUnfiledFiles(projectId);
  const unfiledCount = unfiledFiles?.length ?? 0;

  // Auto-expand root-level folders when data first loads
  const didAutoExpand = useRef(false);
  useEffect(() => {
    if (!folders || didAutoExpand.current) return;
    const rootIds = folders.filter((f) => !(f.fullPath ?? "").includes("/")).map((f) => f.id);
    setExpanded(new Set(rootIds));
    didAutoExpand.current = true;
  }, [folders]);

  // Derive the display name of the selected entry
  const selectedName = useMemo(() => {
    if (!selected) return undefined;
    if (selected === "__unfiled__") return "Unfiled";
    const f = (folders ?? []).find((f) => f.id === selected);
    return f ? ((f.fullPath ?? "").split("/").pop() ?? f.fullPath ?? undefined) : undefined;
  }, [selected, folders]);

  const filteredFolders = useMemo(() => {
    const all = folders ?? [];
    const term = search.trim().toLowerCase();
    if (!term) return all;

    const matchingPaths = new Set<string>();
    for (const f of all) {
      if ((f.fullPath ?? "").toLowerCase().includes(term)) {
        matchingPaths.add(f.fullPath ?? "");
      }
    }

    // Include ancestor paths so the tree renders correctly
    const toInclude = new Set<string>(matchingPaths);
    for (const path of matchingPaths) {
      const parts = path.split("/").filter(Boolean);
      for (let i = 1; i < parts.length; i++) {
        toInclude.add(parts.slice(0, i).join("/"));
      }
    }

    return all.filter((f) => toInclude.has(f.fullPath ?? ""));
  }, [folders, search]);

  const tree = useMemo(() => buildFolderTree(filteredFolders), [filteredFolders]);

  // When searching, expand everything so matches are visible
  const effectiveExpanded = useMemo(() => {
    if (!search.trim()) return expanded;
    return new Set(filteredFolders.map((f) => f.id));
  }, [search, expanded, filteredFolders]);

  function toggleExpand(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (isError) {
    return <ErrorState message={error?.message} onRetry={() => refetch()} />;
  }

  const isSearching = search.trim().length > 0;
  const noResults = isSearching && tree.length === 0;

  return (
    <div className="flex rounded-lg border overflow-hidden h-[calc(100vh-260px)] min-h-[450px]">
      {/* Left panel — folder tree */}
      <div className="w-72 shrink-0 border-r flex flex-col bg-muted/30">
        {/* Search input */}
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search folders…"
              className="h-7 pl-7 text-sm"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {noResults ? (
            <p className="px-2 py-4 text-center text-sm text-muted-foreground">
              No folders match "{search}"
            </p>
          ) : tree.length === 0 && unfiledCount === 0 ? (
            <p className="px-2 py-4 text-center text-sm text-muted-foreground">
              No folders for this project
            </p>
          ) : (
            <div className="space-y-0.5">
              {tree.map((node) => (
                <FolderTreeItem
                  key={node.folder.id}
                  node={node}
                  expanded={effectiveExpanded}
                  selected={selected}
                  onToggle={toggleExpand}
                  onSelect={setSelected}
                />
              ))}

              {/* Unfiled entry — hide when searching */}
              {!isSearching && (
                <div
                  className={cn(
                    "flex w-full items-center rounded-md text-sm transition-colors",
                    selected === "__unfiled__"
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-accent/50",
                  )}
                >
                  <span className="h-8 w-6 shrink-0" />
                  <button
                    type="button"
                    className="flex flex-1 items-center gap-2 py-1.5 pr-2 text-left min-w-0"
                    onClick={() => setSelected("__unfiled__")}
                  >
                    <FileX className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="font-medium flex-1">Unfiled</span>
                    {unfiledCount > 0 && (
                      <Badge variant="secondary" className="ml-auto h-5 px-1.5 text-xs shrink-0">
                        {unfiledCount}
                      </Badge>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right panel — file list */}
      <div className="flex-1 overflow-hidden">
        {selected && selected !== "__unfiled__" ? (
          <FolderUploadZone projectId={projectId} folderId={selected}>
            <FolderFilesPanel
              projectId={projectId}
              selected={selected}
              selectedName={selectedName}
            />
          </FolderUploadZone>
        ) : (
          <div className="h-full overflow-auto p-5">
            <FolderFilesPanel
              projectId={projectId}
              selected={selected}
              selectedName={selectedName}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main export ──

export function DocumentsTab({ projectId }: { projectId: string }) {
  return (
    <Tabs defaultValue="files" className="space-y-4">
      <TabsList className="self-start">
        <TabsTrigger value="files">Files</TabsTrigger>
        <TabsTrigger value="folders">Folders</TabsTrigger>
      </TabsList>
      <TabsContent value="files">
        <FilesSubTab projectId={projectId} />
      </TabsContent>
      <TabsContent value="folders">
        <FoldersSubTab projectId={projectId} />
      </TabsContent>
    </Tabs>
  );
}
